import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import ukMoment from '../../../utils/ukTime';

import Loading from "../../components/Loading";
import DataTable from "../../components/DataTable";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

const formatDate = date => moment(date).format('YYYY-MM-DD');
const displayDate = date => ukMoment(date).format('DD MMM YYYY');
const displayTime = date => ukMoment(date).format('HH:mm');
const normalizeDateParam = value => {
	const parsed = moment(value, [moment.ISO_8601, 'YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY'], true);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
};

const statusLabel = (booking) => {
	if (booking.rowType === 'withheld') return 'Withheld';
	if (booking.cancelled) return 'Cancelled';
	return 'Active';
};

function List() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [bookings, setBookings] = useState([]);
	const [studios, setStudios] = useState([]);
	const [filtersOpen, setFiltersOpen] = useState(false);

	const startParam = normalizeDateParam(searchParams.get('start'));
	const endParam = normalizeDateParam(searchParams.get('end'));
	const start = startParam || formatDate(new Date());
	const end = endParam || formatDate(moment().add(7, 'days'));
	const status = searchParams.get('status') || '';
	const studio = searchParams.get('studio') || '';
	const hasInitialDateParams = searchParams.get('start') && searchParams.get('end');

	const setFilter = (key, value) => {
		const next = new URLSearchParams(searchParams);
		if (value) {
			next.set(key, value);
		} else {
			next.delete(key);
		}

		// Keep the range in order rather than letting an inverted start/end reach the
		// backend (which rejects it) - snap the other bound to match instead.
		if (key === 'start' && value && value > end) {
			next.set('end', value);
		} else if (key === 'end' && value && value < start) {
			next.set('start', value);
		}

		if (key === 'start' || key === 'end') next.delete('range');
		next.delete('time');
		next.set('bookings_page', '1');
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next);
	};

	const setDateRange = (rangeStart, rangeEnd, rangeKey) => {
		const next = new URLSearchParams(searchParams);
		next.set('start', rangeStart);
		next.set('end', rangeEnd);
		next.delete('range');
		next.delete('time');
		next.delete('user');
		next.set('bookings_page', '1');
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next);
	};

	const rangeOptions = useMemo(() => {
		const today = moment();
		return [
			{ key: 'thisMonth', label: 'This Month', start: formatDate(today.clone().startOf('month')), end: formatDate(today.clone().endOf('month')) },
			{ key: 'nextMonth', label: 'Next Month', start: formatDate(today.clone().add(1, 'month').startOf('month')), end: formatDate(today.clone().add(1, 'month').endOf('month')) },
			{ key: 'lastMonth', label: 'Last Month', start: formatDate(today.clone().subtract(1, 'month').startOf('month')), end: formatDate(today.clone().subtract(1, 'month').endOf('month')) },
			{ key: 'thisYear', label: 'This Year', start: formatDate(today.clone().startOf('year')), end: formatDate(today.clone().endOf('year')) },
			{ key: 'nextYear', label: 'Next Year', start: formatDate(today.clone().add(1, 'year').startOf('year')), end: formatDate(today.clone().add(1, 'year').endOf('year')) },
			{ key: 'lastYear', label: 'Last Year', start: formatDate(today.clone().subtract(1, 'year').startOf('year')), end: formatDate(today.clone().subtract(1, 'year').endOf('year')) }
		];
	}, []);

	const activeRange = rangeOptions.find(option => option.start === start && option.end === end)?.key || '';

	const refreshBookings = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await Data.fetchBookings(start, end);
			setBookings(result.data);
		} catch (err) {
			setError(err?.response?.data?.message || 'Could not load bookings. Please check the date filters and try again.');
			setBookings([]);
		} finally {
			setLoading(false);
		}
	}, [end, start]);

	useEffect(() => {
		if (hasInitialDateParams) return;
		const next = new URLSearchParams(searchParams);
		if (!next.get('start')) next.set('start', start);
		if (!next.get('end')) next.set('end', end);
		next.delete('range');
		next.delete('user');
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next);
	}, [end, hasInitialDateParams, searchParams, setSearchParams, start]);

	useEffect(() => {
		if (!searchParams.get('range') && !searchParams.get('user') && !searchParams.get('time')) return;
		const next = new URLSearchParams(searchParams);
		next.delete('range');
		next.delete('user');
		next.delete('time');
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next);
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		refreshBookings();
	}, [refreshBookings]);

	useEffect(() => {
		Data.fetchStudios()
			.then(result => setStudios(result.data.sort((a, b) => a.name.localeCompare(b.name))))
			.catch(err => console.error('Could not load studios for filter:', err));
	}, []);

	const output = bookings.filter(booking => {
		if (status === 'active' && (booking.cancelled || booking.rowType === 'withheld')) return false;
		if (status === 'cancelled' && !booking.cancelled) return false;
		if (status === 'withheld' && booking.rowType !== 'withheld') return false;
		if (studio && booking.session?.Studio?._id !== studio) return false;
		return true;
	});

	const rangeControls = (
		<div className={styles.rangeTabs}>
			{rangeOptions.map(option => (
				<button
					type="button"
					key={option.key}
					className={activeRange === option.key ? styles.activeRangeTab : ''}
					onClick={() => setDateRange(option.start, option.end, option.key)}
				>
					{option.label}
				</button>
			))}
		</div>
	);

	const filterButton = (
		<button
			type="button"
			className={styles.tableFilterButton}
			onClick={() => setFiltersOpen(value => !value)}
			aria-expanded={filtersOpen}
		>
			<span className={styles.filterIcon} />
			Filters
		</button>
	);

	const filters = (
		<>
			<input type="date" value={start} onChange={(event) => setFilter('start', event.target.value)} />
			<input type="date" value={end} onChange={(event) => setFilter('end', event.target.value)} />
			<select value={status} onChange={(event) => setFilter('status', event.target.value)}>
				<option value="">All Statuses</option>
				<option value="active">Active</option>
				<option value="cancelled">Cancelled</option>
				<option value="withheld">Withheld</option>
			</select>
			<select value={studio} onChange={(event) => setFilter('studio', event.target.value)}>
				<option value="">All Studios</option>
				{studios.map((studioOption) => (
					<option key={studioOption._id} value={studioOption._id}>{studioOption.name}</option>
				))}
			</select>
		</>
	);

	const columns = useMemo(() => [
		{
			id: 'date',
			header: 'Date',
			accessor: booking => booking.session.start,
			cell: booking => displayDate(booking.session.start)
		},
		{
			id: 'time',
			header: 'Time',
			accessor: booking => displayTime(booking.session.start),
			cell: booking => `${displayTime(booking.session.start)} - ${displayTime(booking.session.end)}`
		},
		{
			id: 'member',
			header: 'Member',
			accessor: booking => booking.User ? `${booking.User.firstName} ${booking.User.lastName}` : '',
			cell: booking => booking.User ? `${booking.User.firstName} ${booking.User.lastName}` : '-'
		},
		{
			id: 'email',
			header: 'Email',
			accessor: booking => booking.User?.email || '',
			cell: booking => booking.User?.email || '-'
		},
		{
			id: 'studio',
			header: 'Studio',
			accessor: booking => booking.session.Studio.name
		},
		{
			id: 'status',
			header: 'Status',
			accessor: statusLabel,
			cell: booking => <span className={`${styles.statusPill} ${booking.cancelled ? styles.cancelledPill : booking.rowType === 'withheld' ? styles.withheldPill : styles.activePill}`}>{statusLabel(booking)}</span>
		},
		{
			id: 'cancelledAt',
			header: 'Date/Time Cancelled',
			accessor: booking => booking.cancelled || '',
			cell: booking => booking.cancelled ? `${displayDate(booking.cancelled)} ${displayTime(booking.cancelled)}` : '-'
		},
		{
			id: 'details',
			header: '',
			sortable: false,
			search: false,
			cell: booking => booking.rowType === 'withheld' ? '-' : <Link to={booking._id} className="btn btnEmpty btnSmall">View</Link>
		}
	], []);

	if (loading) return <Loading button={false} />;

	if (error) {
		return (
			<div className={styles.fullWidth}>
				<div className={styles.errorPanel}>
					<p>{error}</p>
					<button type="button" className="btn btnEmpty" onClick={refreshBookings}>Try Again</button>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.fullWidth}>
			<DataTable
				title="Bookings"
				description="Check and manage session bookings, cancelled bookings, and withheld slots."
				columns={columns}
				data={output}
				defaultPageSize={50}
				toolbarStart={rangeControls}
				filters={filters}
				filtersOpen={filtersOpen}
				filterButton={filterButton}
				queryPrefix="bookings"
				initialSort="date"
				emptyMessage="There are not any bookings or withheld slots to show."
				rowClassName={booking => booking.cancelled ? styles.cancelledRow : booking.rowType === 'withheld' ? styles.withheldRow : ''}
			/>
		</div>
	);
}

export default List;
